import { useCallback } from 'react';

import {
  isPositionOccupied,
  formatSeatLabel,
  getSeatNumberForCol,
  generateSeatsInZone,
  generateTribuneBlock,
  DEFAULT_NAMING,
  isSkippedCol,
  isSkippedRow,
  isAisleCell,
  relabelSeats,
} from '../utils/seatUtils';

export const useSeatManagement = (
  seats,
  setSeats,
  onSeatsCreate,
  saveToHistory,
  getSeatAtPosition,
  GRID_SIZE,
  GRID_ROWS,
  GRID_COLS,
  selectedZone = null,
  currentZones = [],
  naming = DEFAULT_NAMING,
) => {
  const addSeat = useCallback(
    (row, col, seatType = 'REGULAR', { recordHistory = true } = {}) => {
      if (isSkippedCol(col, naming) || isSkippedRow(row, naming)) return false;
      if (isPositionOccupied(seats, row, col)) return false;

      const newSeat = {
        id: `seat-${Date.now()}-${Math.random()}`,
        row,
        col,
        x: col * GRID_SIZE,
        y: row * GRID_SIZE,
        label: formatSeatLabel(row, col, naming),
        seatType,
        number: getSeatNumberForCol(col, naming, row),
        width: 1.0,
        height: 1.0,
        rotation: 0.0,
        accessibility: seatType === 'WHEELCHAIR',
        isActive: true,
        zoneId: selectedZone?.id || null,
      };

      const updatedSeats = [...seats, newSeat];
      setSeats(updatedSeats);
      onSeatsCreate(updatedSeats);
      if (recordHistory) {
        saveToHistory(updatedSeats, 'Koltuk eklendi', currentZones);
      }
      return true;
    },
    [seats, onSeatsCreate, saveToHistory, setSeats, GRID_SIZE, selectedZone, currentZones, naming],
  );

  const paintSeats = useCallback(
    (positions, seatType = 'REGULAR') => {
      if (!positions?.length) return 0;
      let working = [...seats];
      const added = [];
      for (const pos of positions) {
        if (isSkippedCol(pos.col, naming) || isSkippedRow(pos.row, naming)) {
          continue;
        }
        if (isPositionOccupied(working, pos.row, pos.col)) continue;
        const seat = {
          id: `seat-${Date.now()}-${pos.row}-${pos.col}-${Math.random().toString(36).slice(2, 7)}`,
          row: pos.row,
          col: pos.col,
          x: pos.col * GRID_SIZE,
          y: pos.row * GRID_SIZE,
          label: formatSeatLabel(pos.row, pos.col, naming),
          seatType,
          number: getSeatNumberForCol(pos.col, naming, pos.row),
          width: 1.0,
          height: 1.0,
          rotation: 0.0,
          accessibility: seatType === 'WHEELCHAIR',
          isActive: true,
          zoneId: selectedZone?.id || null,
        };
        working.push(seat);
        added.push(seat);
      }
      if (!added.length) return 0;
      working = relabelSeats(working, naming);
      setSeats(working);
      onSeatsCreate(working);
      saveToHistory(working, `${added.length} koltuk boyandi`, currentZones);
      return added.length;
    },
    [seats, setSeats, onSeatsCreate, saveToHistory, GRID_SIZE, selectedZone, currentZones, naming],
  );

  const eraseSeatsAt = useCallback(
    (positions) => {
      if (!positions?.length) return 0;
      const keys = new Set(positions.map((p) => `${p.row}-${p.col}`));
      const before = seats.length;
      let updated = seats.filter((s) => !keys.has(`${s.row}-${s.col}`));
      if (updated.length === before) return 0;
      updated = relabelSeats(updated, naming);
      setSeats(updated);
      onSeatsCreate(updated);
      saveToHistory(updated, `${before - updated.length} koltuk silindi`, currentZones);
      return before - updated.length;
    },
    [seats, setSeats, onSeatsCreate, saveToHistory, currentZones, naming],
  );

  const removeSeat = useCallback(
    (row, col) => {
      let updatedSeats = seats.filter((seat) => !(seat.row === row && seat.col === col));
      updatedSeats = relabelSeats(updatedSeats, naming);
      setSeats(updatedSeats);
      onSeatsCreate(updatedSeats);
      saveToHistory(updatedSeats, 'Koltuk silindi', currentZones);
    },
    [seats, onSeatsCreate, saveToHistory, setSeats, currentZones, naming],
  );

  const moveSelectedSeats = useCallback(
    (selectedSeats, deltaRow, deltaCol) => {
      if (selectedSeats.length === 0) return;

      const selectedSeatObjects = seats.filter((seat) => {
        const seatKey = `${seat.row}-${seat.col}`;
        return selectedSeats.includes(seatKey);
      });

      const canMove = selectedSeatObjects.every((seat) => {
        const newRow = seat.row + deltaRow;
        const newCol = seat.col + deltaCol;

        if (newRow < 0 || newRow >= GRID_ROWS || newCol < 0 || newCol >= GRID_COLS) {
          return false;
        }

        const existingSeat = getSeatAtPosition(newRow, newCol);
        const isSelectedSeat =
          existingSeat && selectedSeats.includes(`${existingSeat.row}-${existingSeat.col}`);

        return !existingSeat || isSelectedSeat;
      });

      if (!canMove) return null;

      let updatedSeats = seats.map((seat) => {
        const seatKey = `${seat.row}-${seat.col}`;
        if (selectedSeats.includes(seatKey)) {
          const newRow = seat.row + deltaRow;
          const newCol = seat.col + deltaCol;
          return {
            ...seat,
            row: newRow,
            col: newCol,
            x: newCol * GRID_SIZE,
            y: newRow * GRID_SIZE,
            label: formatSeatLabel(newRow, newCol, naming),
            number: getSeatNumberForCol(newCol, naming, newRow),
          };
        }
        return seat;
      });
      updatedSeats = relabelSeats(updatedSeats, naming);

      const newSelectedSeats = selectedSeats.map((seatKey) => {
        const [row, col] = seatKey.split('-').map(Number);
        return `${row + deltaRow}-${col + deltaCol}`;
      });

      setSeats(updatedSeats);
      onSeatsCreate(updatedSeats);
      saveToHistory(updatedSeats, `${selectedSeats.length} koltuk tasindi`);
      return newSelectedSeats;
    },
    [
      seats,
      getSeatAtPosition,
      onSeatsCreate,
      saveToHistory,
      setSeats,
      GRID_SIZE,
      GRID_ROWS,
      GRID_COLS,
      naming,
    ],
  );

  const clearSelectedSeats = useCallback(
    (selectedSeatsList) => {
      const selectedKeys = selectedSeatsList;
      let updatedSeats = seats.filter((seat) => {
        const seatKey = `${seat.row}-${seat.col}`;
        return !selectedKeys.includes(seatKey);
      });
      updatedSeats = relabelSeats(updatedSeats, naming);
      setSeats(updatedSeats);
      onSeatsCreate(updatedSeats);
      saveToHistory(updatedSeats, `Secili alan bosaltildi (${selectedKeys.length})`, currentZones);
    },
    [seats, onSeatsCreate, saveToHistory, setSeats, currentZones, naming],
  );

  const clearAllSeats = useCallback(() => {
    const updatedSeats = [];
    setSeats(updatedSeats);
    onSeatsCreate(updatedSeats);
    saveToHistory(updatedSeats, 'Tum koltuklar temizlendi', currentZones);
  }, [onSeatsCreate, saveToHistory, setSeats, currentZones]);

  const fillEmptyAreaWithSeats = useCallback(
    (getEmptyPositionsInBox, box) => {
      const emptyPositions = getEmptyPositionsInBox(box).filter(
        (pos) =>
          !isSkippedCol(pos.col, naming) &&
          !isSkippedRow(pos.row, naming) &&
          !isAisleCell(pos.row, pos.col, naming),
      );
      if (emptyPositions.length === 0) return;

      const newSeats = emptyPositions.map((pos) => ({
        id: `seat-${Date.now()}-${pos.row}-${pos.col}-${Math.random()}`,
        row: pos.row,
        col: pos.col,
        x: pos.col * GRID_SIZE,
        y: pos.row * GRID_SIZE,
        label: formatSeatLabel(pos.row, pos.col, naming),
        seatType: 'REGULAR',
        number: getSeatNumberForCol(pos.col, naming, pos.row),
        width: 1.0,
        height: 1.0,
        rotation: 0.0,
        accessibility: false,
        isActive: true,
        zoneId: selectedZone?.id || null,
      }));

      let updatedSeats = [...seats, ...newSeats];
      updatedSeats = relabelSeats(updatedSeats, naming);
      setSeats(updatedSeats);
      onSeatsCreate(updatedSeats);
      saveToHistory(updatedSeats, `Bos alan dolduruldu (${newSeats.length} koltuk)`, currentZones);
    },
    [seats, onSeatsCreate, saveToHistory, setSeats, GRID_SIZE, currentZones, selectedZone, naming],
  );

  const fillZoneWithSeats = useCallback(
    (zone, options = {}) => {
      if (!zone) return 0;
      const generated = generateSeatsInZone(zone, seats, GRID_COLS, GRID_ROWS, options).filter(
        (s) =>
          !isSkippedCol(s.col, naming) &&
          !isSkippedRow(s.row, naming) &&
          !isAisleCell(s.row, s.col, naming),
      );
      if (generated.length === 0) return 0;
      let updatedSeats = relabelSeats(
        [
          ...seats,
          ...generated.map((s) => ({
            ...s,
            label: formatSeatLabel(s.row, s.col, naming),
            number: getSeatNumberForCol(s.col, naming, s.row),
          })),
        ],
        naming,
      );
      setSeats(updatedSeats);
      onSeatsCreate(updatedSeats);
      saveToHistory(
        updatedSeats,
        `${zone.name} zone dolduruldu (${generated.length} koltuk)`,
        currentZones,
      );
      return generated.length;
    },
    [seats, setSeats, onSeatsCreate, saveToHistory, GRID_COLS, GRID_ROWS, currentZones, naming],
  );

  const fillZoneTribune = useCallback(
    (zone, options = {}) => {
      if (!zone) return 0;
      const generated = generateTribuneBlock(zone, seats, GRID_COLS, GRID_ROWS, options).filter(
        (s) =>
          !isSkippedCol(s.col, naming) &&
          !isSkippedRow(s.row, naming) &&
          !isAisleCell(s.row, s.col, naming),
      );
      if (generated.length === 0) return 0;
      let updatedSeats = relabelSeats(
        [
          ...seats,
          ...generated.map((s) => ({
            ...s,
            label: formatSeatLabel(s.row, s.col, naming),
            number: getSeatNumberForCol(s.col, naming, s.row),
          })),
        ],
        naming,
      );
      setSeats(updatedSeats);
      onSeatsCreate(updatedSeats);
      saveToHistory(
        updatedSeats,
        `${zone.name} tribun eklendi (${generated.length} koltuk)`,
        currentZones,
      );
      return generated.length;
    },
    [seats, setSeats, onSeatsCreate, saveToHistory, GRID_COLS, GRID_ROWS, currentZones, naming],
  );

  return {
    addSeat,
    paintSeats,
    eraseSeatsAt,
    removeSeat,
    moveSelectedSeats,
    clearSelectedSeats,
    clearAllSeats,
    fillEmptyAreaWithSeats,
    fillZoneWithSeats,
    fillZoneTribune,
  };
};
